import { autoUpdate, offset, shift, useFloating } from "@floating-ui/react";
import { faCog, faDatabase } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover } from "@headlessui/react";
import { useAtom } from "jotai";
import { basemaps } from "../../basemaps";
import { useMap } from "../../hooks/mapState";
import { automaticPanAndZoomAtom } from "../../state";
import { ControlButton } from "../../ui/MapControls";
import { ToolBarDropdown } from "../../ui/MapControlsV2";
import { Checkbox, MenuCategory } from "../../ui/Menu";

interface BasemapSelectorProps {
    basemap: string | number | null;
    onBasemapChange: (basemap: string | number | null) => void;
}

const BasemapSelector = ({
    basemap,
    onBasemapChange,
}: BasemapSelectorProps) => {
    const [autoMove, setAutoMove] = useAtom(automaticPanAndZoomAtom);
    return (
        <div className="w-44" key={basemap}>
            <MenuCategory>Basemaps</MenuCategory>
            <div className="text-sm flex flex-col gap-2 p-2 whitespace-nowrap">
                <label className="flex gap-2 items-center">
                    <input
                        type="radio"
                        name="basemapSelector"
                        checked={basemap === null}
                        onChange={() => onBasemapChange(null)}
                        className="text-ae-blue-500"
                    />
                    Automatic
                </label>
                {Object.entries(basemaps).map(
                    ([key, value]) =>
                        value && (
                            <div key={key} className="flex gap-2 items-center">
                                <input
                                    id={key}
                                    type="radio"
                                    name="basemapSelector"
                                    checked={basemap === key}
                                    onChange={() => onBasemapChange(key)}
                                    className="text-ae-blue-500"
                                />
                                <label htmlFor={key}>{value.displayName}</label>
                            </div>
                        ),
                )}
                <hr />
                <MenuCategory>Behavior</MenuCategory>
                <Checkbox
                    label="Automatic pan/zoom"
                    checked={autoMove}
                    onChange={() => setAutoMove((i) => !i)}
                />
            </div>
        </div>
    );
};

interface BasemapControlProps {
    basemap: string | number | null;
    toggleBasemap: (basemap: string | number | null) => void;
}

export const BasemapControl = (props: BasemapControlProps) => {
    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "top-end",
        middleware: [
            offset({
                mainAxis: 2,
                crossAxis: -45,
            }),
            shift(),
        ],
        whileElementsMounted: autoUpdate,
    });

    return (
        <Popover className="relative">
            {({ open }) => (
                <>
                    <Popover.Button ref={refs.setReference}>
                        <ControlButton
                            title="Change basemap"
                            active={open}
                            className="w-8 rounded"
                        >
                            <FontAwesomeIcon icon={faCog} className="w-4" />
                        </ControlButton>
                    </Popover.Button>
                    <Popover.Panel
                        ref={refs.setFloating}
                        style={floatingStyles}
                        className="bg-white rounded p-2 border border-ae-gray-250 z-50"
                    >
                        <BasemapSelector
                            basemap={props.basemap}
                            onBasemapChange={props.toggleBasemap}
                        />
                    </Popover.Panel>
                </>
            )}
        </Popover>
    );
};

interface BasemapControlsProps {
    className?: string;
}

export const BasemapControls = (props: BasemapControlsProps) => {
    const { basemap, setMapState } = useMap("mainMap");

    const handleBasemapChange = (newBasemap: string | null) => {
        setMapState((ms) => ({ ...ms, basemap: newBasemap }));
    };

    return (
        <ToolBarDropdown
            enabled={true}
            icon={faDatabase}
            label="Map"
            className={props.className}
        >
            <BasemapSelector
                basemap={basemap}
                onBasemapChange={handleBasemapChange}
            />
        </ToolBarDropdown>
    );
};
